import React from "react";
import { Package } from "lucide-react";
import AIStudioTool from "./AIStudioTool";

const FIELDS = [
  { key: "product_name", label: "Product name", required: true, wide: true,
    placeholder: "e.g. Sunrise Oats — Vanilla Almond",
    hint: "The consumer-facing product name." },

  { key: "brand_name", label: "Brand name (rendered on-pack)",
    placeholder: "e.g. Sunrise",
    hint: "This exact text will be printed on the packaging — spell it as it should appear." },

  { key: "category", label: "Category", required: true,
    placeholder: "e.g. Overnight oats, functional beverage, kombucha…",
    hint: "One line — what shelf does this sit on?" },

  { key: "format", label: "Packaging format", type: "select",
    options: [
      { value: "box",     label: "Carton / box"  },
      { value: "pouch",   label: "Stand-up pouch" },
      { value: "bottle",  label: "Bottle"        },
      { value: "jar",     label: "Jar"           },
      { value: "can",     label: "Can"           },
      { value: "tube",    label: "Tube"          },
      { value: "carton",  label: "Milk carton"   },
      { value: "bag",     label: "Chip / snack bag" },
    ] },

  { key: "target_audience", label: "Target audience", type: "textarea", rows: 2,
    placeholder: "e.g. Millennial founders, urban wellness moms, Gen-Z gym-goers…",
    hint: "Who is this brand talking to? One sentence is enough.", wide: true, maxLength: 400 },

  { key: "key_benefits", label: "Key benefits", type: "textarea", rows: 2,
    placeholder: "e.g. 20g protein, zero refined sugar, 5-minute breakfast…",
    hint: "The 3-5 benefits you would put on the front of pack.", wide: true, maxLength: 600 },

  { key: "hero_claim", label: "Hero on-pack claim",
    placeholder: "e.g. 20G PROTEIN · Cold-brewed · Made in Brooklyn",
    hint: "A short, punchy line to render as secondary type on the packaging. Optional.",
    maxLength: 80 },

  { key: "aspirational_brands", label: "Aspirational brands", wide: true,
    placeholder: "e.g. Olipop, Erewhon, Sunday Rains, Ghia, Poppi",
    hint: "Comma-separated brands whose aesthetic to evoke (not copy). Anchors visual DNA.",
    maxLength: 200 },

  { key: "photography_style", label: "Photography style", type: "select",
    options: [
      { value: "studio_product",      label: "Studio product — hero, clean seamless" },
      { value: "editorial_lifestyle", label: "Editorial lifestyle — in-scene" },
      { value: "retail_shelf",        label: "Retail shelf — in-context" },
      { value: "flat_lay",            label: "Flat lay — top-down" },
      { value: "hero_hero",           label: "Hero-hero — dramatic single subject" },
    ] },

  { key: "material_finish", label: "Material & finish", type: "select",
    options: [
      { value: "matte",              label: "Matte" },
      { value: "gloss",              label: "Gloss" },
      { value: "soft_touch",         label: "Soft-touch coated" },
      { value: "kraft",              label: "Kraft (natural)" },
      { value: "metallic_foil",      label: "Metallic foil stamped" },
      { value: "embossed",           label: "Embossed / debossed" },
      { value: "recycled_uncoated",  label: "Recycled uncoated" },
      { value: "clear_glass",        label: "Clear glass" },
      { value: "frosted_glass",      label: "Frosted glass" },
    ] },

  { key: "typography_direction", label: "Typography direction", type: "select",
    options: [
      { value: "modern_grotesk",       label: "Modern grotesk (Inter, Söhne)" },
      { value: "editorial_serif",      label: "Editorial serif (Freight, Canela)" },
      { value: "hand_drawn",           label: "Hand-drawn / marker" },
      { value: "vintage_script",       label: "Vintage script" },
      { value: "all_caps_industrial",  label: "All-caps industrial (Aeonik Mono)" },
      { value: "custom_wordmark",      label: "Custom wordmark" },
    ] },

  { key: "style", label: "Style direction",
    placeholder: "e.g. modern minimal, editorial, luxury heritage, retro nostalgic",
    hint: "Free-form mood — how should it feel overall?" },

  { key: "color_notes", label: "Colour notes",
    placeholder: "e.g. warm terracotta + cream, or emerald + gold",
    hint: "Optional. Leave blank to let AI decide." },

  { key: "count",   label: "Concepts to generate", type: "number", min: 1, max: 4,
    hint: "How many mockups to render (1-4)." },

  { key: "quality", label: "Render quality", type: "select",
    options: [
      { value: "low",    label: "Low — fastest previews" },
      { value: "medium", label: "Medium — balanced" },
      { value: "high",   label: "High — portfolio-ready (recommended)" },
    ],
    hint: "Higher quality burns more image credits but yields sharper detail." },
];

const DEFAULTS = {
  product_name: "",
  brand_name: "",
  category: "",
  format: "box",
  target_audience: "",
  key_benefits: "",
  hero_claim: "",
  aspirational_brands: "",
  photography_style: "studio_product",
  material_finish: "matte",
  typography_direction: "modern_grotesk",
  style: "modern, minimal, editorial",
  color_notes: "",
  count: 2,
  quality: "high",
};

// Admin-only test scenarios — cover the four aesthetic corners we most want
// to QA when tuning the packaging prompt: premium editorial beverage, retro
// snack, wellness supplement, and playful pet product. Values slot 1:1 into
// the fields above so the whole form fills with one click.
const DEMO_INPUTS = [
  {
    label: "Premium editorial beverage",
    hint: "Oat-milk carton · warm cream + terracotta · Ghia/Oatly-adjacent",
    values: {
      product_name: "Sunrise Oat — Vanilla Chamomile",
      brand_name: "Sunrise Oat",
      category: "Barista-grade oat milk",
      format: "carton",
      target_audience: "urban wellness founders, 28-42, morning ritual crowd, third-wave coffee bar regulars",
      key_benefits: "gut-friendly · barista-grade froth · organic Canadian oats · zero gums",
      hero_claim: "Slow-brewed. Real oats.",
      aspirational_brands: "Oatly, Ghia, Cometeer, Aesop",
      photography_style: "editorial_lifestyle",
      material_finish: "recycled_uncoated",
      typography_direction: "editorial_serif",
      style: "premium editorial minimal, quiet-luxury, warm daylight",
      color_notes: "warm cream #F5EAD8, deep terracotta #B04A2A, soft ink",
      count: 2, quality: "high",
    },
  },
  {
    label: "Retro snack — Gen-Z chip bag",
    hint: "70s bubble type · loud saturated color · Doritos-adjacent energy",
    values: {
      product_name: "Chip Riot — Cool Ranch Storm",
      brand_name: "Chip Riot",
      category: "Bold-flavored corn tortilla chips",
      format: "bag",
      target_audience: "Gen-Z snackers 18-26, TikTok-first, sneaker heads, gamer nights",
      key_benefits: "extra bold seasoning · non-GMO corn · MSG-free · 4g protein / serving",
      hero_claim: "COOL RANCH · STORM MODE",
      aspirational_brands: "Doritos, Takis, Flamin' Hot, Trip drinks",
      photography_style: "hero_hero",
      material_finish: "gloss",
      typography_direction: "all_caps_industrial",
      style: "retro 70s bubble-letter meets rave poster, high-saturation, chrome-tinged",
      color_notes: "electric blue #1A6DFF, cyan #00E0FF, hot pink #FF2A6D accents on jet black bag",
      count: 2, quality: "high",
    },
  },
  {
    label: "Wellness supplement",
    hint: "Amber glass bottle · minimalist medical · Athletic-Greens vibe",
    values: {
      product_name: "Field & Marrow — Daily Adaptogen Blend",
      brand_name: "Field & Marrow",
      category: "Adaptogen powder supplement",
      format: "jar",
      target_audience: "high-performing 30-45 professionals, biohackers, morning routine devotees",
      key_benefits: "12 clinical adaptogens · third-party tested · organic · 30 servings",
      hero_claim: "12 CLINICAL ADAPTOGENS",
      aspirational_brands: "Athletic Greens, Sakara, Moon Juice",
      photography_style: "studio_product",
      material_finish: "frosted_glass",
      typography_direction: "modern_grotesk",
      style: "medical-clean, apothecary meets modern lab, cool serene",
      color_notes: "sage green #7A8C6A, soft bone #F4EFE8, matte black cap",
      count: 2, quality: "high",
    },
  },
  {
    label: "Playful pet CPG",
    hint: "Dog treat pouch · illustrated · Beau Life / Fable-adjacent",
    values: {
      product_name: "Rufflebite — Peanut & Pumpkin Chews",
      brand_name: "Rufflebite",
      category: "Grain-free soft-baked dog treats",
      format: "pouch",
      target_audience: "millennial dog parents, Instagram-active, treat-their-dog-as-family, urban apartment life",
      key_benefits: "grain-free · limited ingredient · soft-baked in Vermont · training-size chews",
      hero_claim: "SOFT-BAKED · GRAIN-FREE",
      aspirational_brands: "Fable, Beau Life, Bark",
      photography_style: "flat_lay",
      material_finish: "kraft",
      typography_direction: "hand_drawn",
      style: "warm illustrative, hand-drawn confetti of pups + pumpkins, welcoming",
      color_notes: "harvest orange #F27A1A, warm cream #F5EAD8, forest ink",
      count: 2, quality: "high",
    },
  },
];

export default function AIStudioPackaging() {
  return (
    <AIStudioTool
      toolSlug="packaging"
      name="Packaging Designer"
      Icon={Package}
      tagline="Draft shelf-ready packaging concepts from your brand brief. Warren's AI writes a Pentagram-grade design brief, then GPT Image renders photorealistic mockups you can hand to a designer. Drop reference images to anchor the aesthetic."
      fields={FIELDS}
      endpoint="/ai-studio/packaging/generate"
      defaultValues={DEFAULTS}
      demoInputs={DEMO_INPUTS}
      cta="Generate packaging concepts"
      historyLabel="Your packaging concepts"
      supportsReferences
      footerNote="Concepts render in about 45–90 seconds. Warren will bake the brand name and hero claim into the packaging — spell them exactly."
    />
  );
}
